import { preloadImage } from '~/utils/preload-image';
import { rotateImageFile } from '~/utils/rotate-image';

/**
 * Rotates an already-uploaded X-ray by 90° clockwise:
 * downloads the current bytes via the server proxy, rotates them on a canvas,
 * uploads the rotated bytes back to overwrite the S3 object at the same key.
 * Returns a fresh signed URL pointing to the new content. The new URL is
 * pre-loaded into the browser cache before resolving so the consumer can
 * switch the displayed `<img>` without a flicker between the optimistic CSS
 * rotation and the final rotated bytes.
 */
export async function rotateExistingXray(xrayId: string, filename: string): Promise<string> {
  const blobRes = await fetch(`/api/xrays/${xrayId}/blob`);
  if (!blobRes.ok) {
    throw new Error(`Failed to fetch X-ray blob (${blobRes.status})`);
  }
  const blob = await blobRes.blob();

  // X-rays are JPEG in practice; fall back to that MIME if the proxy didn't
  // supply a Content-Type, so canvas.toBlob in rotateImageFile gets a valid type.
  const file = new File([blob], filename, { type: blob.type || 'image/jpeg' });
  const rotated = await rotateImageFile(file, 90);

  const fd = new FormData();
  fd.append('file', rotated);

  const postRes = await fetch(`/api/xrays/${xrayId}/rotate`, {
    method: 'POST',
    body: fd,
  });
  if (!postRes.ok) {
    throw new Error(`Failed to save rotation (${postRes.status})`);
  }

  const data = (await postRes.json()) as { ok?: boolean; signedUrl?: unknown };
  if (typeof data.signedUrl !== 'string') {
    throw new Error('Rotate response missing signedUrl');
  }

  await preloadImage(data.signedUrl);

  return data.signedUrl;
}
